import React from 'react';
import { 
  Building2, GraduationCap, MapPin, Users, Award, 
  ExternalLink, Mail, ShieldCheck, Laptop, BookOpen 
} from 'lucide-react';
import { FACULTY_MEMBERS, INSTITUTION_INFO } from '../data/portalData';

export const FacultyAndCampuses = () => {
  return (
    <section id="executive-faculty" className="py-20 bg-slate-50 border-b-2 border-slate-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-20">
        
        {/* Sub-Section 1: Executive Faculty */}
        <div>
          <div className="text-center max-w-3xl mx-auto mb-14">
            <div className="inline-flex items-center space-x-2 text-xs font-black uppercase tracking-widest text-[#0B2644] bg-amber-50 px-3 py-1 rounded-lg border border-[#7A470E]/40 mb-3 font-sc">
              <Users className="w-3.5 h-3.5 text-[#7A470E]" />
              <span>World-Class Scholar-Practitioners</span>
            </div>
            <h2 className="text-3xl sm:text-5xl font-black text-[#0B2644] font-sc">
              Executive Faculty & Research Chairs
            </h2>
            <p className="mt-4 text-base text-slate-600">
              Our distinguished faculty combine doctorate pedigree from prestigious global universities with 
              decades of boardroom, regulatory, and public sector leadership experience.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {FACULTY_MEMBERS.map((fac, idx) => (
              <div 
                key={idx}
                className="translucent-card-light rounded-3xl p-5 shadow-sm hover:shadow-xl transition-all flex flex-col justify-between hover:border-[#7A470E] hover:scale-[1.02]"
              >
                <div>
                  <div className="relative mb-4 text-center">
                    <img 
                      src={fac.avatar} 
                      alt={fac.name}
                      className="w-24 h-24 rounded-full mx-auto object-cover border-2 border-[#7A470E] shadow-sm" 
                    />
                    <span className="absolute bottom-0 right-1/2 translate-x-7 bg-[#0B2644] text-white p-1 rounded-full text-[10px]" title="Accredited Academic">
                      <ShieldCheck className="w-3.5 h-3.5 text-amber-300" />
                    </span>
                  </div>

                  <div className="text-center">
                    <h3 className="font-black text-lg text-slate-900 font-sc">
                      {fac.name}
                    </h3>
                    <p className="text-xs font-bold text-[#7A470E] mt-0.5 font-sc">
                      {fac.rank}
                    </p>
                    <p className="text-[11px] text-slate-500 mt-0.5">
                      {fac.qualifications}
                    </p>
                  </div>

                  <div className="mt-4 pt-3 border-t border-slate-100 text-xs text-slate-600 space-y-2">
                    <div>
                      <span className="font-bold text-slate-800 block">Core Specialisation:</span>
                      <span className="text-[11px] text-slate-600">{fac.speciality}</span>
                    </div>

                    <div>
                      <span className="font-bold text-slate-800 block">Key Modules:</span>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {fac.coursesAssigned.map(c => (
                          <span key={c} className="text-[10px] bg-slate-100 text-[#0B2644] font-mono font-bold px-1.5 py-0.5 rounded border border-slate-200">
                            {c}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mt-4 pt-3 border-t border-slate-100 flex items-center justify-between text-xs text-slate-500">
                  <a 
                    href={`mailto:${fac.email}`}
                    className="flex items-center hover:text-[#0B2644] transition-colors text-[11px] font-bold"
                  >
                    <Mail className="w-3 h-3 mr-1 text-[#7A470E]" />
                    <span>Contact Chair</span>
                  </a>
                  <span className="text-[10px] bg-amber-50 text-[#5C3306] font-bold px-2 py-0.5 rounded-full border border-amber-200">
                    Active
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Sub-Section 2: Executive Campuses & State-of-the-Art Facilities */}
        <div>
          <div className="text-center max-w-3xl mx-auto mb-14">
            <div className="inline-flex items-center space-x-2 text-xs font-black uppercase tracking-widest text-[#0B2644] bg-amber-50 px-3 py-1 rounded-lg border border-[#7A470E]/40 mb-3 font-sc">
              <Building2 className="w-3.5 h-3.5 text-[#7A470E]" />
              <span>Campus Location · Main Campus Ikot Akpaden</span>
            </div>
            <h2 className="text-3xl sm:text-5xl font-black text-[#0B2644] font-sc">
              Main Campus at Ikot Akpaden, Mkpat Enin
            </h2>
            <p className="mt-4 text-base text-slate-600">
              Akwa Ibom State University Business School is situated at the University Main Campus in Ikot Akpaden, 
              Mkpat Enin Local Government Area—delivering executive education in a serene, state-of-the-art environment with hybrid digital connectivity.
            </p>
          </div>

          <div className="max-w-4xl mx-auto">
            {(INSTITUTION_INFO?.campuses || []).map((camp, idx) => (
              <div 
                key={idx}
                className="translucent-card-light rounded-3xl p-8 sm:p-10 shadow-lg hover:shadow-2xl transition-all flex flex-col justify-between group hover:border-[#7A470E]"
              >
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <span className="px-3 py-1 text-xs font-black rounded-lg bg-[#0B2644] text-white font-sc">
                      {camp.type}
                    </span>
                    <span className="text-xs text-slate-500 flex items-center">
                      <MapPin className="w-3.5 h-3.5 mr-1 text-[#7A470E]" />
                      {camp.location}
                    </span>
                  </div>

                  <h3 className="text-2xl font-black text-slate-900 group-hover:text-[#0B2644] transition-colors font-sc">
                    {camp.name}
                  </h3>

                  <p className="text-xs sm:text-sm text-slate-600 mt-2 leading-relaxed">
                    {camp.description}
                  </p>

                  <div className="mt-6 pt-4 border-t border-slate-100 space-y-2">
                    <span className="text-xs font-bold text-slate-800 uppercase tracking-wider font-sc block text-[#7A470E]">
                      Key Facilities:
                    </span>
                    <div className="grid grid-cols-2 gap-2 text-xs text-slate-700">
                      {camp.features.map((feat, fIdx) => (
                        <div key={fIdx} className="flex items-center space-x-1.5">
                          <span className="w-1.5 h-1.5 rounded-full bg-[#7A470E]" />
                          <span>{feat}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="mt-6 pt-4 border-t border-slate-100 flex items-center justify-between text-xs text-slate-500 font-mono">
                  <span>State-of-the-Art Executive Suites</span>
                  <span className="font-bold text-[#0B2644] font-sc">Hybrid Stream Active</span>
                </div>
              </div>
            ))}
          </div>
        </div>

      </div>
    </section>
  );
};
